import React, { useState, useEffect } from "react";
import {
  Search,
  MapPin,
  Briefcase,
  Filter,
  DollarSign,
  User,
  BookOpen,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/Api.js";

const JobOffersList = () => {
  const [filters, setFilters] = useState({
    keyword: "",
    location: "",
    contractType: "all",
    experience: "all",
    salaryMin: "",
    salaryMax: "",
    skills: [],
    industries: [],
  });
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [jobOffers, setJobOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
  });
  const navigate = useNavigate();

  // Liste prédéfinie de compétences et industries
  const skillsList = [
    "JavaScript",
    "React",
    "Node.js",
    "Python",
    "Java",
    "SQL",
    "Cloud",
    "Machine Learning",
    "Figma",
    "UI/UX",
    "DevOps",
    "Cybersécurité",
  ];

  const industriesList = [
    "Tech",
    "Finance",
    "Santé",
    "E-commerce",
    "Marketing Digital",
    "Éducation",
    "Média",
    "Automobile",
    "Énergie",
    "Télécommunications",
  ];

  // Fonction pour récupérer les offres d'emploi
  const fetchJobOffers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/jobOffer/");

      if (response.data.success) {
        setJobOffers(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError("Erreur lors du chargement des offres");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
      console.error("Error fetching job offers:", err);
    } finally {
      setLoading(false);
    }
  };

  // Charger les offres au montage du composant
  useEffect(() => {
    fetchJobOffers();
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleSkillToggle = (skill) => {
    setFilters((prevFilters) => {
      const currentSkills = prevFilters.skills;
      const newSkills = currentSkills.includes(skill)
        ? currentSkills.filter((s) => s !== skill)
        : [...currentSkills, skill];

      return {
        ...prevFilters,
        skills: newSkills,
      };
    });
  };

  const handleIndustryToggle = (industry) => {
    setFilters((prevFilters) => {
      const currentIndustries = prevFilters.industries;
      const newIndustries = currentIndustries.includes(industry)
        ? currentIndustries.filter((i) => i !== industry)
        : [...currentIndustries, industry];

      return {
        ...prevFilters,
        industries: newIndustries,
      };
    });
  };

  // Fonction pour formater le niveau d'expérience
  const formatExperienceLevel = (level) => {
    switch (level) {
      case 1:
        return "Débutant";
      case 2:
        return "2-5 ans";
      case 3:
        return "5+ ans";
      default:
        return "Non spécifié";
    }
  };

  // Fonction pour formater le type de contrat
  const formatContractType = (type) => {
    switch (type) {
      case "cdi":
        return "CDI";
      case "cdd":
        return "CDD";
      case "freelance":
        return "Freelance";
      case "internship":
        return "Stage";
      default:
        return type?.toUpperCase() || "Non spécifié";
    }
  };

  // Composant de filtres réutilisable pour desktop et mobile
  const FilterPanel = () => (
    <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">
          <User className="inline-block mr-2" /> Expérience
        </h2>
        <select
          name="experience"
          value={filters.experience}
          onChange={handleFilterChange}
          className="w-full py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">Toute expérience</option>
          <option value="1">Débutant</option>
          <option value="2">2-5 ans</option>
          <option value="3">5+ ans</option>
        </select>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">
          <BookOpen className="inline-block mr-2" /> Compétences
        </h2>
        <div className="flex flex-wrap gap-2">
          {skillsList.map((skill) => (
            <button
              key={skill}
              onClick={() => handleSkillToggle(skill)}
              className={`px-2 py-1 rounded-lg text-sm transition ${
                filters.skills.includes(skill)
                  ? "bg-primary-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              }`}
            >
              {skill}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">
          Industries
        </h2>
        <div className="flex flex-wrap gap-2">
          {industriesList.map((industry) => (
            <button
              key={industry}
              onClick={() => handleIndustryToggle(industry)}
              className={`px-2 py-1 rounded-lg text-sm transition ${
                filters.industries.includes(industry)
                  ? "bg-primary-600 text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              }`}
            >
              {industry}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Composant de chargement
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center py-8">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  // Composant d'erreur
  const ErrorMessage = () => (
    <div className="text-center py-8">
      <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
        <p className="font-semibold">Erreur</p>
        <p>{error}</p>
        <button
          onClick={fetchJobOffers}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
        >
          Réessayer
        </button>
      </div>
    </div>
  );

  return (
    <div className="px-4 py-6 dark:bg-gray-900 dark:text-gray-100 min-h-screen">
      <main className="container mx-auto px-4 sm:px-6 py-16">
        {/* Mobile Filter Toggle */}
        <div className="md:hidden mb-4">
          <button
            onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
            className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700 transition"
          >
            <Filter />
            {isMobileFilterOpen
              ? "Masquer les filtres"
              : "Afficher les filtres"}
          </button>
        </div>

        {/* Filtres mobiles - affichés uniquement lorsque isMobileFilterOpen est true */}
        {isMobileFilterOpen && (
          <div className="md:hidden mb-6">
            <FilterPanel />
          </div>
        )}

        {/* Barre de recherche principale */}
        <div className="mb-6 bg-gray-100 dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="col-span-1 md:col-span-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  name="keyword"
                  placeholder="Métier, mots-clés..."
                  value={filters.keyword}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="col-span-1 md:col-span-1">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  name="location"
                  placeholder="Ville, département..."
                  value={filters.location}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="col-span-1 md:col-span-1">
              <select
                name="contractType"
                value={filters.contractType}
                onChange={handleFilterChange}
                className="w-full py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Tous les contrats</option>
                <option value="cdi">CDI</option>
                <option value="cdd">CDD</option>
                <option value="freelance">Freelance</option>
                <option value="internship">Stage</option>
              </select>
            </div>
            <div className="col-span-1 md:col-span-1">
              <button
                onClick={fetchJobOffers}
                className="w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
              >
                Rechercher
              </button>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Filtres additionnels - Desktop */}
          <div className="hidden md:block col-span-1">
            <FilterPanel />
          </div>

          {/* Résultats */}
          <div className="col-span-1 md:col-span-3">
            {loading ? (
              <LoadingSpinner />
            ) : error ? (
              <ErrorMessage />
            ) : jobOffers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  Aucune offre d'emploi trouvée
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Informations sur les résultats */}
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {pagination.total} offre{pagination.total > 1 ? "s" : ""}{" "}
                    trouvée{pagination.total > 1 ? "s" : ""}
                  </p>
                </div>

                {/* Liste des offres */}
                {jobOffers.map((job) => (
<div 
                    key={job._id} 
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm hover:shadow-md transition"
                  >
                    <div className="flex gap-4">
                      {/* Image section */}
                      <div className="flex-shrink-0 m-2.5">
                        <div className="w-64 h-full bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                          {job.company.profile_picture ? (
                            <img 
                              src={process.env.REACT_APP_BASE_URL + '/' +job.company.profile_picture} 
                              alt={`Logo ${job.company?.name || 'Entreprise'}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Briefcase className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Content section */}
                      <div className="flex-1">
                        <div className="mb-3">
                          <h3 className="text-xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                            {job.title?.title || 'Titre non disponible'}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400">
                            {job.company?.name || 'Entreprise non spécifiée'}
                          </p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3 text-sm">
                          <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900 px-2 py-1 text-xs font-medium text-blue-800 dark:text-blue-300 ring-1 ring-blue-600/20 ring-inset">
                            <MapPin className="mr-1" size={16} />
                            {job.address?.city || 'Ville non spécifiée'}, {job.address?.wilaya || 'Pays non spécifié'}
                          </span>
                          <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900 px-2 py-1 text-xs font-medium text-green-800 dark:text-green-300 ring-1 ring-green-600/20 ring-inset">
                            <Briefcase className="mr-1" size={16} />
                            {formatContractType(job.contractType)}
                          </span>
                          <span className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900 px-2 py-1 text-xs font-medium text-purple-800 dark:text-purple-300 ring-1 ring-purple-600/20 ring-inset">
                            <User className="mr-1" size={16} />
                            {formatExperienceLevel(job.experienceLevel)}
                          </span>
                        </div>

                        {/* Compétences requises */}
                        {job.required_skills && job.required_skills.length > 0 && (
                          <div className="mb-3">
                            <div className="flex flex-wrap gap-1">
                              {job.required_skills.map((skill, index) => (
                                <span 
                                  key={index}
                                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-xs"
                                >
                                  {skill.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3">
                          {job.description || 'Description non disponible'}
                        </p>

                        {/* Tags */}
                        {job.tags && job.tags.length > 0 && (
                          <div className="mb-3">
                            <div className="flex flex-wrap gap-1">
                              {job.tags.map((tag, index) => (
                                <span 
                                  key={index}
                                  className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300 rounded-md text-xs"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex justify-between items-center">
                          <div className="text-gray-500 dark:text-gray-400 text-sm">
                            <p>Publié le {new Date(job.createdAt).toLocaleDateString()}</p>
                            {job.endAt && (
                              <p>Expire le {new Date(job.endAt).toLocaleDateString()}</p>
                            )}
                          </div>
                          <button 
                            onClick={() => {navigate(`/job-offers/${job._id}`)}}  
                            className="px-4 py-2 border border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400 rounded-lg hover:bg-primary-600 dark:hover:bg-primary-400 hover:text-white dark:hover:text-gray-800 transition"
                          >
                            Voir l'offre
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobOffersList;
